<!-- src/views/AppointmentsView.html -->
<div class="view-container">
  <!-- Header -->
  <div class="header-section">
    <div>
      <h1 class="view-title">Gestione Appuntamenti</h1>
      <p class="view-desc">Visualizza, ricerca e gestisci le prenotazioni dei tuoi clienti.</p>
    </div>
    <button class="btn-primary rounded-soft" @click="openCreateDrawer">
      <Plus :size="18" />
      <span>Nuovo Appuntamento</span>
    </button>
  </div>

  <!-- Barra di Ricerca -->
  <div class="toolbar-section mb-4">
    <div class="toolbar flex gap-4 mb-4">
      <div class="search-box">
        <Search class="search-icon" :size="18" />
        <input v-model="searchQuery" class="search-input" placeholder="Cerca cliente o nota..." />
      </div>
    
      <select v-model="selectedOperator" class="form-group select-operator">
        <option value="">Tutti gli operatori</option>
        <option v-for="op in operators" :key="op.id" :value="op.id">
          {{ op.name }}
        </option>
      </select>
    </div>
  </div>

  <!-- Tabella Appuntamenti -->
  <div class="table-container rounded-soft shadow-soft">
    <table class="data-table table-appointments">
      <thead>
        <tr>
          <th class="col-cliente">Cliente</th>
          <th class="col-data">Data</th>
          <th class="col-ora hide-mobile">Orario</th>
          <th class="col-status hide-mobile">Status</th>
          <th class="col-importo hide-mobile">Totale</th>
          <th class="col-importo hide-mobile">Pagato</th>
          <th class="col-stato">Pagamento</th>
          <th class="col-actions">Azioni</th>
        </tr>
      </thead>
      <tbody>
        <!-- Stato Vuoto -->
        <tr v-if="filteredAppointments.length === 0">
          <td colspan="8" class="empty-state">
            {{ searchQuery ? 'Nessun appuntamento corrisponde alla ricerca.' : 'Nessun appuntamento in programma.' }}
          </td>
        </tr>

        <!-- Ciclo Appuntamenti -->
        <tr v-for="appt in filteredAppointments" :key="appt.id">
          <td class="font-medium text-light whitespace-pre-line">
            {{ appt.cliente }}
          </td>
          <td class="text-muted">{{ formatDate(appt.data) }}</td>
          <td class="text-muted hide-mobile">
            {{ appt.orainizio }} - {{ appt.orafine }}
          </td>
          <td class="hide-mobile">
            <!-- Badge dinamico per lo Status (Confermato, In Attesa, Annullato) -->
            <span class="status-badge" :class="`status-${appt.status.toLowerCase().replace(/\s+/g, '-')}`">
              {{ appt.status_display }} 
            </span>
          </td>
          <td class="font-medium text-light hide-mobile">
            {{ formatCurrency(appt.importoTotale) }}
          </td>
          <td class="font-medium hide-mobile" :class="appt.importoPagato >= appt.importoTotale ? 'text-success' : 'text-warning'">
            {{ formatCurrency(appt.importoPagato) }}
          </td>
          <td>
            <!-- Badge dinamico per lo Stato Pagamento -->
            <span :class="getStatusClass(appt.payment_status)">
              {{ appt.payment_status_display }}
            </span>
            <!--
            <span class="stato-badge" :class="`stato-${appt.stato.toLowerCase().replace(/\s+/g, '-')}`">
              {{ appt.stato }}
            </span>
            -->
          </td>
          <td class="actions-cell">
            <button class="btn-icon rounded-soft btn-edit" @click="openEditDrawer(appt)" title="Modifica">
              <Edit :size="16" />
            </button>
            <button class="btn-icon rounded-soft btn-delete" @click="deleteAppointment(appt.id)" title="Elimina">
              <Trash2 :size="16" />
            </button>
            <button class="btn-icon rounded-soft btn-cancel" @click="handleAdminAction(appt.id, 'cancel')" title="Annulla">
              <X :size="16" />
            </button>
            <button class="btn-icon rounded-soft btn-reschedule" @click="promptReschedule(appt)" title="Sposta">
              <Calendar :size="16" /> 
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Drawer (Pannello Laterale) -->
  <div class="drawer-overlay" :class="{ 'is-visible': isDrawerOpen }" @click="closeDrawer"></div>
  
  <div class="drawer" :class="{ 'is-open': isDrawerOpen }">
    <div class="drawer-header border-bottom">
      <h2>{{ isEditing ? 'Modifica Appuntamento' : 'Nuovo Appuntamento' }}</h2>
      <button class="btn-close" @click="closeDrawer">
        <X :size="20" />
      </button>
    </div>

    <div class="drawer-body">
      <form @submit.prevent="saveAppointment" id="appt-form">
        <!-- Input Cliente con Testo Libero -->
        <div class="form-group">
          <label for="cliente">Cliente *</label>
          <textarea 
            id="cliente" 
            v-model="form.cliente" 
            required 
            rows="2" 
            placeholder="Nome, Cognome e riferimenti..."
            class="rounded-soft"
          ></textarea>
        </div>

        <div class="form-group">
          <label for="data">Data Appuntamento *</label>
          <input type="date" id="data" v-model="form.data" required />
        </div>

        <div class="form-row">
          <div class="form-group half-width">
            <label for="orainizio">Dalle ore *</label>
            <input type="time" id="orainizio" v-model="form.orainizio" required />
          </div>
          <div class="form-group half-width">
            <label for="orafine">Alle ore *</label>
            <input type="time" id="orafine" v-model="form.orafine" required />
          </div>
        </div>

        <div class="form-group" v-if="isEditing">
          <label>Operatori coinvolti:</label>
          <ul class="operator-list">
            <li v-for="item in currentBookingItems" :key="item.id">
              <strong>{{ item.service_name }}</strong>: 
              {{ item.operator_name || 'Non assegnato' }}
            </li>
          </ul>
        </div>

        <div class="form-group">
          <label for="status">Status Appuntamento</label>
          <select id="status" v-model="form.status" class="rounded-soft">
            <option value="pending">In Attesa</option>
            <option value="confirmed">Confermato</option>
            <option value="cancelled">Annullato</option>
          </select>
        </div>

        <div class="form-row">
          <div class="form-group half-width">
            <label for="importoTotale">Importo Totale (€)</label>
            <input type="number" step="0.01" id="importoTotale" v-model="form.importoTotale" />
          </div>
          <div class="form-group half-width">
            <label for="importoPagato">Acconto/Saldato (€)</label>
            <input type="number" step="0.01" id="importoPagato" v-model="form.importoPagato" />
          </div>
        </div>

        <div class="form-group">
          <label for="stato">Stato Pagamento</label>
          <select id="stato" v-model="form.stato">
            <option value="to_pay">Da pagare</option>
            <option value="partial">Pagamento parziale</option>
            <option value="paid">Saldato</option>
            <option value="refunded">Rimborsato</option>
          </select>
        </div>

        <div class="form-group">
          <label for="note">Note Interne</label>
          <textarea id="note" v-model="form.note" rows="3" placeholder="Dettagli trattamento, preferenze..."></textarea>
        </div>
      </form>
    </div>

    <div class="drawer-footer border-top">
      <button type="button" class="btn-secondary rounded-soft" @click="closeDrawer">Annulla</button>
      <button type="submit" form="appt-form" class="btn-primary rounded-soft">
        {{ isEditing ? 'Aggiorna' : 'Salva' }}
      </button>
    </div>
  </div>
</div>